import { useEffect, useState } from 'react';
import { apiService } from '../services/api.service';
import { useTenantStore } from '../stores/tenant.store';

type Tab = 'suppliers' | 'invoices' | 'payments' | 'expenses' | 'costCenters';

const money = (n: number | string) =>
  Number(n ?? 0).toLocaleString('es-AR', { style: 'currency', currency: 'ARS' });

export function PayablesPage() {
  const legalEntityId = useTenantStore((s) => s.legalEntityId);
  const [tab, setTab] = useState<Tab>('suppliers');
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [supplierForm, setSupplierForm] = useState({ taxId: '', legalName: '', tradeName: '' });
  const [invoiceForm, setInvoiceForm] = useState({
    supplierId: '',
    invoiceNumber: '',
    invoiceDate: new Date().toISOString().slice(0, 10),
    netAmount: '',
    taxAmount: '0',
    taxDiscriminated: false,
    description: '',
  });
  const [expenseForm, setExpenseForm] = useState({
    description: '',
    amount: '',
    expenseDate: new Date().toISOString().slice(0, 10),
    method: 'CASH',
    cashRegisterId: '',
  });
  const [ccForm, setCcForm] = useState({ code: '', name: '' });
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [registers, setRegisters] = useState<any[]>([]);

  const load = async () => {
    if (!legalEntityId) return;
    setLoading(true);
    setError('');
    try {
      if (tab === 'suppliers') {
        const list = await apiService.listSuppliers(legalEntityId);
        setRows(list);
        setSuppliers(list);
      } else if (tab === 'invoices') {
        setRows(await apiService.listSupplierInvoices(legalEntityId));
        setSuppliers(await apiService.listSuppliers(legalEntityId));
      } else if (tab === 'payments') {
        setRows(await apiService.listSupplierPayments(legalEntityId));
      } else if (tab === 'expenses') {
        setRows(await apiService.listExpenses(legalEntityId));
        setRegisters(await apiService.getCashRegisters(undefined, legalEntityId));
      } else if (tab === 'costCenters') {
        setRows(await apiService.listCostCenters(legalEntityId));
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || e.message || 'Error al cargar');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [legalEntityId, tab]);

  const tabs: { id: Tab; label: string }[] = [
    { id: 'suppliers', label: 'Proveedores' },
    { id: 'invoices', label: 'Facturas' },
    { id: 'payments', label: 'Pagos AP' },
    { id: 'expenses', label: 'Gastos' },
    { id: 'costCenters', label: 'Centros de costo' },
  ];

  const createSupplier = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!legalEntityId) return;
    setBusySafe(async () => {
      const supplier = await apiService.createSupplier(supplierForm);
      await apiService.linkCompanySupplier({ legalEntityId, supplierId: supplier.id });
      setSupplierForm({ taxId: '', legalName: '', tradeName: '' });
      await load();
    });
  };

  const createInvoice = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!legalEntityId) return;
    const net = Number(invoiceForm.netAmount);
    const tax = Number(invoiceForm.taxAmount || 0);
    setBusySafe(async () => {
      const inv = await apiService.createSupplierInvoice({
        legalEntityId,
        supplierId: invoiceForm.supplierId,
        invoiceNumber: invoiceForm.invoiceNumber,
        invoiceDate: invoiceForm.invoiceDate,
        netAmount: net,
        taxAmount: tax,
        totalAmount: net + tax,
        taxDiscriminated: invoiceForm.taxDiscriminated,
        description: invoiceForm.description || undefined,
        lines: [
          {
            description: invoiceForm.description || 'Concepto',
            quantity: 1,
            unitAmount: net,
            netAmount: net,
            taxAmount: tax,
            totalAmount: net + tax,
          },
        ],
      });
      await apiService.registerSupplierInvoice(inv.id);
      setInvoiceForm((f) => ({ ...f, invoiceNumber: '', netAmount: '', taxAmount: '0', description: '' }));
      await load();
    });
  };

  const createExpense = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!legalEntityId) return;
    const amount = Number(expenseForm.amount);
    setBusySafe(async () => {
      const exp = await apiService.createExpense({
        legalEntityId,
        description: expenseForm.description,
        amount,
        taxAmount: 0,
        totalAmount: amount,
        expenseDate: expenseForm.expenseDate,
        method: expenseForm.method,
        cashRegisterId: expenseForm.cashRegisterId,
      });
      await apiService.postExpense(exp.id);
      setExpenseForm((f) => ({ ...f, description: '', amount: '' }));
      await load();
    });
  };

  const createCostCenter = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!legalEntityId) return;
    setBusySafe(async () => {
      await apiService.createCostCenter({ legalEntityId, ...ccForm });
      setCcForm({ code: '', name: '' });
      await load();
    });
  };

  const setBusySafe = async (fn: () => Promise<void>) => {
    setLoading(true);
    setError('');
    try {
      await fn();
    } catch (e: any) {
      setError(e?.response?.data?.message || e.message || 'Error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Cuentas a pagar y gastos</h1>
        <p className="text-sm text-slate-600 mt-1">
          Proveedores, facturas, pagos, gastos directos y centros de costo por empresa.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-3 py-1.5 text-sm rounded-md ${
              tab === t.id ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error ? <p className="text-sm text-red-600">{error}</p> : null}
      {!legalEntityId ? (
        <p className="text-sm text-amber-700">Seleccioná una empresa en el selector de tenant.</p>
      ) : null}

      {tab === 'suppliers' && (
        <form onSubmit={createSupplier} className="grid gap-2 sm:grid-cols-4 items-end">
          <label className="text-sm">
            CUIT
            <input
              className="mt-1 w-full border rounded-md px-2 py-1.5"
              value={supplierForm.taxId}
              onChange={(e) => setSupplierForm({ ...supplierForm, taxId: e.target.value })}
              required
            />
          </label>
          <label className="text-sm sm:col-span-2">
            Razón social
            <input
              className="mt-1 w-full border rounded-md px-2 py-1.5"
              value={supplierForm.legalName}
              onChange={(e) => setSupplierForm({ ...supplierForm, legalName: e.target.value })}
              required
            />
          </label>
          <button
            type="submit"
            disabled={loading}
            className="px-3 py-2 text-sm bg-slate-900 text-white rounded-md disabled:opacity-50"
          >
            Alta + vincular
          </button>
        </form>
      )}

      {tab === 'invoices' && (
        <form onSubmit={createInvoice} className="grid gap-2 sm:grid-cols-3 items-end">
          <label className="text-sm sm:col-span-2">
            Proveedor
            <select
              className="mt-1 w-full border rounded-md px-2 py-1.5"
              value={invoiceForm.supplierId}
              onChange={(e) => setInvoiceForm({ ...invoiceForm, supplierId: e.target.value })}
              required
            >
              <option value="">Seleccionar…</option>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.legalName} ({s.taxId})
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            N° factura
            <input
              className="mt-1 w-full border rounded-md px-2 py-1.5"
              value={invoiceForm.invoiceNumber}
              onChange={(e) => setInvoiceForm({ ...invoiceForm, invoiceNumber: e.target.value })}
              required
            />
          </label>
          <label className="text-sm">
            Fecha
            <input
              type="date"
              className="mt-1 w-full border rounded-md px-2 py-1.5"
              value={invoiceForm.invoiceDate}
              onChange={(e) => setInvoiceForm({ ...invoiceForm, invoiceDate: e.target.value })}
              required
            />
          </label>
          <label className="text-sm">
            Neto
            <input
              type="number"
              step="0.01"
              className="mt-1 w-full border rounded-md px-2 py-1.5"
              value={invoiceForm.netAmount}
              onChange={(e) => setInvoiceForm({ ...invoiceForm, netAmount: e.target.value })}
              required
            />
          </label>
          <label className="text-sm">
            IVA
            <input
              type="number"
              step="0.01"
              className="mt-1 w-full border rounded-md px-2 py-1.5"
              value={invoiceForm.taxAmount}
              onChange={(e) => setInvoiceForm({ ...invoiceForm, taxAmount: e.target.value })}
            />
          </label>
          <label className="text-sm flex items-center gap-2 pt-6">
            <input
              type="checkbox"
              checked={invoiceForm.taxDiscriminated}
              onChange={(e) => setInvoiceForm({ ...invoiceForm, taxDiscriminated: e.target.checked })}
            />
            Discriminar IVA (libro COMPRAS)
          </label>
          <button
            type="submit"
            disabled={loading}
            className="px-3 py-2 text-sm bg-slate-900 text-white rounded-md disabled:opacity-50 sm:col-span-3"
          >
            Crear y registrar
          </button>
        </form>
      )}

      {tab === 'expenses' && (
        <form onSubmit={createExpense} className="grid gap-2 sm:grid-cols-4 items-end">
          <label className="text-sm sm:col-span-2">
            Descripción
            <input
              className="mt-1 w-full border rounded-md px-2 py-1.5"
              value={expenseForm.description}
              onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
              required
            />
          </label>
          <label className="text-sm">
            Monto
            <input
              type="number"
              step="0.01"
              className="mt-1 w-full border rounded-md px-2 py-1.5"
              value={expenseForm.amount}
              onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
              required
            />
          </label>
          <label className="text-sm">
            Caja
            <select
              className="mt-1 w-full border rounded-md px-2 py-1.5"
              value={expenseForm.cashRegisterId}
              onChange={(e) => setExpenseForm({ ...expenseForm, cashRegisterId: e.target.value })}
              required
            >
              <option value="">Seleccionar…</option>
              {registers.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.code} — {r.name || r.number}
                </option>
              ))}
            </select>
          </label>
          <button
            type="submit"
            disabled={loading}
            className="px-3 py-2 text-sm bg-slate-900 text-white rounded-md disabled:opacity-50 sm:col-span-4"
          >
            Crear y postear
          </button>
        </form>
      )}

      {tab === 'costCenters' && (
        <form onSubmit={createCostCenter} className="grid gap-2 sm:grid-cols-4 items-end">
          <label className="text-sm">
            Código
            <input
              className="mt-1 w-full border rounded-md px-2 py-1.5"
              value={ccForm.code}
              onChange={(e) => setCcForm({ ...ccForm, code: e.target.value })}
              required
            />
          </label>
          <label className="text-sm sm:col-span-2">
            Nombre
            <input
              className="mt-1 w-full border rounded-md px-2 py-1.5"
              value={ccForm.name}
              onChange={(e) => setCcForm({ ...ccForm, name: e.target.value })}
              required
            />
          </label>
          <button
            type="submit"
            disabled={loading}
            className="px-3 py-2 text-sm bg-slate-900 text-white rounded-md disabled:opacity-50"
          >
            Crear
          </button>
        </form>
      )}

      <div className="overflow-x-auto border border-slate-200 rounded-md">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-left">
            <tr>
              {tab === 'suppliers' && (
                <>
                  <th className="px-3 py-2">CUIT</th>
                  <th className="px-3 py-2">Nombre</th>
                  <th className="px-3 py-2">Estado</th>
                </>
              )}
              {tab === 'invoices' && (
                <>
                  <th className="px-3 py-2">N°</th>
                  <th className="px-3 py-2">Proveedor</th>
                  <th className="px-3 py-2">Total</th>
                  <th className="px-3 py-2">Estado</th>
                </>
              )}
              {tab === 'payments' && (
                <>
                  <th className="px-3 py-2">Fecha</th>
                  <th className="px-3 py-2">Monto</th>
                  <th className="px-3 py-2">Método</th>
                  <th className="px-3 py-2">Estado</th>
                </>
              )}
              {tab === 'expenses' && (
                <>
                  <th className="px-3 py-2">Fecha</th>
                  <th className="px-3 py-2">Descripción</th>
                  <th className="px-3 py-2">Total</th>
                  <th className="px-3 py-2">Estado</th>
                </>
              )}
              {tab === 'costCenters' && (
                <>
                  <th className="px-3 py-2">Código</th>
                  <th className="px-3 py-2">Nombre</th>
                  <th className="px-3 py-2">Activo</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100">
                {tab === 'suppliers' && (
                  <>
                    <td className="px-3 py-2 font-mono">{r.taxId}</td>
                    <td className="px-3 py-2">{r.legalName}</td>
                    <td className="px-3 py-2">{r.isActive ? 'Activo' : 'Inactivo'}</td>
                  </>
                )}
                {tab === 'invoices' && (
                  <>
                    <td className="px-3 py-2">{r.invoiceNumber}</td>
                    <td className="px-3 py-2">{r.supplier?.legalName ?? r.supplierId}</td>
                    <td className="px-3 py-2">{money(r.totalAmount)}</td>
                    <td className="px-3 py-2">{r.status}</td>
                  </>
                )}
                {tab === 'payments' && (
                  <>
                    <td className="px-3 py-2">{String(r.paymentDate).slice(0, 10)}</td>
                    <td className="px-3 py-2">{money(r.amount)}</td>
                    <td className="px-3 py-2">{r.method}</td>
                    <td className="px-3 py-2">{r.status}</td>
                  </>
                )}
                {tab === 'expenses' && (
                  <>
                    <td className="px-3 py-2">{String(r.expenseDate).slice(0, 10)}</td>
                    <td className="px-3 py-2">{r.description}</td>
                    <td className="px-3 py-2">{money(r.totalAmount)}</td>
                    <td className="px-3 py-2">{r.status}</td>
                  </>
                )}
                {tab === 'costCenters' && (
                  <>
                    <td className="px-3 py-2 font-mono">{r.code}</td>
                    <td className="px-3 py-2">{r.name}</td>
                    <td className="px-3 py-2">{r.isActive ? 'Sí' : 'No'}</td>
                  </>
                )}
              </tr>
            ))}
            {!loading && rows.length === 0 ? (
              <tr>
                <td className="px-3 py-6 text-slate-500" colSpan={4}>
                  Sin registros
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}
